<template>
  <div class="page">
    <!-- 顶部导航栏：返回按钮 + 标题 -->
    <div class="nav-bar">
      <div class="nav-back" @click="goBack">
        <!-- 返回箭头 -->
        <svg viewBox="0 0 24 24">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </div>
      <div class="nav-title">兑奖管理</div>
    </div>

    <!-- 最上面：开奖结果提示文字（蓝框红字，提醒以官网为准） -->
    <div class="notice-bar">最终开奖结果以官网为准</div>

    <!-- 中间：兑奖操作栏（手动兑奖 / 预约兑奖） -->
    <div class="prize-actions">
      <div class="action-item" @click="handleAction('manual')">
        <!-- 手动兑奖图标 -->
        <svg viewBox="0 0 24 24">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
        <span>手动兑奖</span>
      </div>
      <!-- 中间分割竖线 -->
      <div class="action-divider"></div>
      <div class="action-item" @click="handleAction('reserve')">
        <!-- 预约兑奖图标（日历+时钟） -->
        <svg viewBox="0 0 24 24">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <circle cx="12" cy="15" r="2" />
        </svg>
        <span>预约兑奖</span>
      </div>
    </div>

    <!-- 下面：待兑奖列表查询。先选彩票类型，再按类型分页查询 -->
    <!-- 彩票类型切换标签：默认快乐8 -->
    <div class="type-tabs">
      <div
        v-for="t in typeList"
        :key="t.value"
        class="type-tab"
        :class="{ active: lotteryType === t.value }"
        @click="switchType(t.value)"
      >
        {{ t.label }}
      </div>
    </div>

    <!-- 待兑奖列表 -->
    <div class="wait-list">
      <div v-for="row in rows" :key="row.id" class="wait-item">
        <!-- 期号 + 待兑奖状态 -->
        <div class="wait-head">
          <span class="wait-expect">第 {{ row.betNumIssue }} 期</span>
          <span class="tag tag-waiting">待兑奖</span>
        </div>

        <!-- 标签行：投注方式（单式/复式） + 玩法（如 7+1） -->
        <div class="wait-tags">
          <span class="tag tag-blue">{{ betTypeText(row.betType) }}</span>
          <span v-if="row.playType" class="tag tag-orange">{{ row.playType }}</span>
        </div>

        <!-- 号码球：betNumDTO.redNum 主号码红色，blueNum 辅助号码（如双色球蓝球）蓝色 -->
        <div class="wait-nums">
          <span v-for="(n, i) in mainNums(row.betNumDTO)" :key="'m' + i" class="num-ball">{{ n }}</span>
          <template v-if="extraNums(row.betNumDTO).length">
            <span class="num-plus">+</span>
            <span v-for="(n, i) in extraNums(row.betNumDTO)" :key="'e' + i" class="num-ball blue">{{ n }}</span>
          </template>
        </div>

        <!-- 投注时间 -->
        <div class="wait-time">投注时间：{{ row.betTime }}</div>
        <!-- 开奖时间 -->
        <div class="wait-time">开奖时间：{{ row.drawTime }}</div>
      </div>

      <!-- 空状态 -->
      <div v-if="!loading && rows.length === 0" class="empty">暂无待兑奖记录</div>

      <!-- 加载更多：点一次多取一页，取完提示没有更多 -->
      <div
        v-if="rows.length > 0"
        class="load-more"
        @click="loadMore"
      >
        {{ hasMore ? (loading ? '加载中...' : '加载更多') : '— 没有更多了 —' }}
      </div>
    </div>

    <!-- 请求失败提示 -->
    <div v-if="errorMsg" class="error-msg">{{ errorMsg }}</div>
  </div>
</template>

<script>
// 导入待兑奖分页查询接口
import { getPendingNumPage } from '@/api/lottery'
// 彩票类型枚举统一从工具文件引入
import { lotteryTypeList } from '@/utils/lottery'

export default {
  name: 'PrizeManageView',
  data() {
    return {
      // 当前选中的彩票类型，默认双色球（请求参数名 lotteryType）
      lotteryType: 'ssq',
      // 彩票类型枚举（统一维护在 @/utils/lottery）
      typeList: lotteryTypeList,
      rows: [],     // 当前列表数据（加载更多时往后追加）
      total: 0,     // 后端返回的总条数
      pageNum: 1,   // 当前页码
      pageSize: 10, // 每页条数
      loading: false,
      errorMsg: ''
    }
  },
  computed: {
    // 已加载条数少于总数，说明还能继续加载
    hasMore() {
      return this.rows.length < this.total
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    // 点击操作栏：manual=手动兑奖，reserve=预约兑奖，都跳转到兑奖页（用 prizeType 区分）
    handleAction(type) {
      this.$router.push({ path: '/num-prize', query: { prizeType: type } })
    },

    // 投注方式编码转中文：single=单式，multiple复式；未知值原样显示
    betTypeText(value) {
      const map = { single: '单式', multiple: '复式' }
      return map[value] || value || ''
    },

    // 主号码：取投注号码对象 betNumDTO 里的 redNum，按逗号拆成数组
    mainNums(betNumDTO) {
      return (betNumDTO && betNumDTO.redNum ? betNumDTO.redNum : '')
        .split(',').filter(Boolean)
    },
    // 辅助号码：取 betNumDTO 里的 blueNum（如双色球蓝球），没有则返回空数组
    extraNums(betNumDTO) {
      return (betNumDTO && betNumDTO.blueNum ? betNumDTO.blueNum : '')
        .split(',').filter(Boolean)
    },

    // 请求待兑奖列表
    async fetchList() {
      this.loading = true
      this.errorMsg = ''
      try {
        const res = await getPendingNumPage({
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          lotteryType: this.lotteryType
        })
        // 后端返回格式：{ code: "200", data: { total, rows }, msg, success }
        if (res.code === '200' || res.success) {
          this.rows = this.rows.concat((res.data && res.data.rows) || [])
          this.total = (res.data && res.data.total) || 0
        } else {
          this.errorMsg = res.msg || '请求失败，请重试'
        }
      } catch (err) {
        this.errorMsg = '网络错误，请稍后重试'
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    // 切换彩票类型：清空数据后重新从第一页请求
    switchType(value) {
      if (this.lotteryType === value) return
      this.lotteryType = value
      this.rows = []
      this.total = 0
      this.pageNum = 1
      this.fetchList()
    },

    // 加载下一页，追加到列表
    loadMore() {
      if (!this.hasMore || this.loading) return
      this.pageNum += 1
      this.fetchList()
    },

    // 返回主页
    goBack() {
      this.$router.push('/home')
    }
  }
}
</script>

<style scoped>
/* 页面整体：与其他子页面一致的手机门户风格 */
.page {
  width: 100%;
  max-width: 480px;
  min-height: 100%;
  margin: 0 auto;
  background: #f5f6fa;
}

/* ===== 顶部导航栏 ===== */
.nav-bar {
  position: relative;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-back {
  position: absolute;
  left: 10px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.nav-back svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: #fff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.nav-title {
  color: #fff;
  font-size: 16px;
}

/* ===== 最上面：开奖结果提示条（蓝框红字） ===== */
.notice-bar {
  margin: 12px;
  padding: 14px;
  border: 1.5px solid #409eff;
  border-radius: 10px;
  background: #fff;
  color: #f56c6c;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  letter-spacing: 1px;
}

/* ===== 中间：兑奖操作栏（红框，两项平分） ===== */
.prize-actions {
  margin: 0 12px 12px;
  padding: 16px 0;
  border: 1.5px solid #f56c6c;
  border-radius: 10px;
  background: #fff;
  display: flex;
  align-items: center;
}

.action-item {
  flex: 1; /* 手动兑奖、预约兑奖各占一半 */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #f56c6c;
  font-size: 16px;
  cursor: pointer;
}

.action-item:active {
  opacity: 0.7; /* 按下去的反馈 */
}

.action-item svg {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: #f56c6c;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* 两个操作之间的竖分割线 */
.action-divider {
  width: 1px;
  height: 22px;
  background: #f56c6c;
  opacity: 0.5;
}

/* ===== 类型切换标签 ===== */
.type-tabs {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  /* 类型超过 4 个时横向滑动查看，一屏最多显示 4 个 */
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* iOS 滑动惯性 */
  scrollbar-width: none; /* Firefox 隐藏滚动条 */
}

/* Chrome/Safari 隐藏滚动条但保留滑动能力 */
.type-tabs::-webkit-scrollbar {
  display: none;
}

.type-tab {
  /* 固定占 1/4 宽度，一屏正好 4 个；超出部分横向滑动，不被挤压 */
  flex: 0 0 25%;
  box-sizing: border-box;
  text-align: center;
  padding: 12px 4px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
}

/* 选中态：主题色文字 + 底部短横线 */
.type-tab.active {
  color: #667eea;
  font-weight: bold;
}

.type-tab.active::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: 28px;
  height: 3px;
  border-radius: 2px;
  background: #667eea;
}

/* ===== 待兑奖列表 ===== */
.wait-list {
  padding: 12px;
}

.wait-item {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
}

.wait-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.wait-expect {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
}

/* 标签行 */
.wait-tags {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}

.tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 18px;
}

.tag-blue {
  background: #ecf0ff;
  color: #667eea;
}

/* 玩法标签：橙色（如 7+1） */
.tag-orange {
  background: #fdf3e6;
  color: #e6a23c;
}

/* 待兑奖状态：橙色标签 */
.tag-waiting {
  background: #fdf6ec;
  color: #e6a23c;
}

/* 号码球：允许换行（快乐8复式号码较多） */
.wait-nums {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.num-ball {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f56c6c; /* 主号码：红色球 */
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.num-ball.blue {
  background: #409eff; /* 辅助号码：蓝色球 */
}

.num-plus {
  color: #909399;
  font-weight: bold;
}

/* 投注时间 */
.wait-time {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}

/* 空状态 */
.empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 40px 0;
}

/* 加载更多 */
.load-more {
  text-align: center;
  color: #667eea;
  font-size: 13px;
  padding: 14px 0;
  cursor: pointer;
}

/* 请求失败提示 */
.error-msg {
  text-align: center;
  color: #f56c6c;
  font-size: 13px;
  padding: 20px 0;
}

/* 手机端适配：号码球略小，保证一行多放几个 */
@media screen and (max-width: 480px) {
  .num-ball {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }
}
</style>
